import { ReactElement, useEffect, useRef, useState } from 'react';
import useStyles from '../styles/customers-hub-table';
import {
  Box,
  Checkbox,
  Chip,
  debounce,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { Pagination } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app';
import { SmallCircularProgress } from 'common/components/loaders';
import AppSnackbar from 'components/snackbar/snackbar';
import { CustomerReviewHubDto, FilterRequest } from '../model/customer-review-hub';
import { formatPhoneNumber } from 'common/utils';
import { format } from 'date-fns';
import EmailLink from 'common/components/buttons/email-link';
import CustomerReviewHubAPI from '../api/customers-review-hub-api';
import { fetchSelectedCustomer } from 'components/customer/components/Customers/customersSlice';
import { DmsTypeEnum } from 'common/enums/DmsTypeEnum';

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): 1 | -1 | 0 {
  if (orderBy != 'id') {
    if (b[orderBy] < a[orderBy] || (b[orderBy] == null && a[orderBy])) {
      return -1;
    }
    if (b[orderBy] > a[orderBy] || (a[orderBy] == null && b[orderBy])) {
      return 1;
    }
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | boolean | string | Date | null },
  b: { [key in Key]: number | boolean | string | Date | null },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

let searchString = '';
export default function CustomerReviewHubTable({
  customerSearchText,
  filterRequest,
  refreshGrid,
  selectedCustomers,
  setSelectedCustomers,
}: {
  customerSearchText: string;
  filterRequest: FilterRequest;
  refreshGrid: boolean;
  selectedCustomers: CustomerReviewHubDto[];
  setSelectedCustomers(val: CustomerReviewHubDto[]): void;
}): ReactElement {
  const classes: any = useStyles();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof CustomerReviewHubDto>('id');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [rows, setRows] = useState<any>([]);
  const [count, setCount] = useState<number>(0);
  const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  const customerDetaillsUpdated = useAppSelector((state) => state.customers.customerDetailsUpdated);

  const dispatch = useAppDispatch();
  const [showSnackBar] = AppSnackbar.useSnackBar();

  const [isLoading, SetIsLoading] = useState<any>(1);
  const [apiLoader, setApiLoader] = useState<boolean>(false);
  const debouncedSearchCustomer = useRef(
    debounce(() => {
      fetchCustomersForReviewHub();
    }, 1000),
  ).current;

  useEffect(() => {
    searchString = customerSearchText;

    if (isLoading == 1) SetIsLoading(0);
    if (isLoading == 0) debouncedSearchCustomer();
  }, [customerSearchText, debouncedSearchCustomer]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof CustomerReviewHubDto): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setSelectedCustomers(rows);
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectSingleClick = (event: React.ChangeEvent<HTMLInputElement>, customer: any): void => {
    let selected = [...selectedCustomers];
    if (event.target.checked) {
      selected.push(customer);
      setSelectedCustomers(selected);
    } else {
      selected = selected.filter((x) => x.id !== customer.id);
      setSelectedCustomers(selected);
    }
  };

  const fetchCustomersForReviewHub = async (): Promise<void> => {
    try {
      setApiLoader(true);
      const customersReviewHubResp = await CustomerReviewHubAPI.getCustomersForReviewHub(
        filterRequest,
        currentPage,
        pageSize,
        searchString,
      );
      if (customersReviewHubResp) {
        setRows(customersReviewHubResp.customers);
        setCurrentPage(customersReviewHubResp.pageNumber);
        setCount(customersReviewHubResp.count);
      }
    } catch (error: any) {
      //showSnackBar({ type: 'error', message: 'Something went wrong' });
    }
    setApiLoader(false);
  };

  useEffect(() => {
    fetchCustomersForReviewHub();
  }, [currentPage, filterRequest, refreshGrid]);

  useEffect(() => {
    if (customerDetaillsUpdated) {
      fetchCustomersForReviewHub();
    }
  }, [customerDetaillsUpdated]);

  const handleChangePage = (event: unknown, newPage: number): void => {
    setCurrentPage(newPage);
  };

  // Commented out the function as not required in the table for now
  // function splitByCapitalLetters(field: string) {
  //   return field?.split(/(?=[A-Z])/).join(' ');
  // }

  const isSelected = (id: number): boolean => !!selectedCustomers.find((x: any) => x.id === id);

  const emptyRows = pageSize - rows?.length;

  interface HeadCell {
    disablePadding: boolean;
    id: keyof CustomerReviewHubDto;
    label: string;
    numeric: boolean;
  }

  const headCells: HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
    },
    {
      id: 'organizationName',
      numeric: false,
      disablePadding: false,
      label: 'Organization',
    },
    {
      id: 'cellPhone',
      numeric: false,
      disablePadding: false,
      label: 'Cell Phone',
    },
    {
      id: 'emailAddress',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    },
    {
      id: 'targetSource',
      numeric: false,
      disablePadding: false,
      label: 'Source',
    },
    {
      id: 'customerNumber',
      numeric: false,
      disablePadding: false,
      label: 'Customer ID',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Creation Date',
    },
    // Hidding the status column for now as not required in the table
    // {
    //   id: 'customerStatus',
    //   numeric: false,
    //   disablePadding: false,
    //   label: 'Status',
    // },
  ];

  interface EnhancedTableProps {
    classes: any;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof CustomerReviewHubDto) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    order: Order;
    orderBy: string;
    selectedCustomersCount: number;
  }

  function EnhancedTableHead(props: EnhancedTableProps): ReactElement {
    const { classes, order, orderBy, onRequestSort, onSelectAllClick, selectedCustomersCount } = props;
    const createSortHandler =
      (property: keyof CustomerReviewHubDto) =>
      (event: React.MouseEvent<unknown>): void => {
        onRequestSort(event, property);
      };
    let tableHeads = [...headCells];
    const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
    if (dmsTypeId !== DmsTypeEnum.Aspen) {
      tableHeads = tableHeads.filter((x) => x.id != 'customerNumber');
    }
    if (dmsTypeId === DmsTypeEnum.Infinity) {
      tableHeads = tableHeads.filter((x) => x.id != 'organizationName');
    }

    const selectAllCustomersHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
      onSelectAllClick(event, event.target.checked);
    };

    return (
      <TableHead style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              checked={selectedCustomersCount > 0 && selectedCustomersCount === rows?.length}
              style={{ padding: '0px', margin: '0px' }}
              onChange={selectAllCustomersHandler}
            />
          </TableCell>
          {tableHeads.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'default'}
              style={{ whiteSpace: 'nowrap', overflow: 'elipses' }}
              sortDirection={orderBy === headCell.id ? order : false}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}>
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer style={{ maxHeight: '65vh' }}>
          <Table className={classes.table} size="small" stickyHeader>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              selectedCustomersCount={selectedCustomers?.length}
              onSelectAllClick={handleSelectAllClick}
            />
            {!apiLoader && (
              <TableBody>
                {rows?.length > 0 ? (
                  stableSort(rows, getComparator(order, orderBy)).map((row: any, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover className={classes.tableRow} tabIndex={-1} key={row.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            className="customercheckbox"
                            onClick={(event: any) => handleSelectSingleClick(event, row)}
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': labelId, id: 'customerCheckbox_' + index }}
                          />
                        </TableCell>
                        <TableCell
                          onClick={() => {
                            if (row.id) {
                              dispatch(fetchSelectedCustomer(row.id));
                            }
                          }}
                          component="th"
                          id={labelId}
                          className={classes.nameCell}
                          scope="row">
                          {row.name && row.name.length > 30 ? row.name.slice(0, 30) + '...' : row.name}
                          {row.isOrganization && row.ccOrganizationName && (
                            <Chip
                              label={row.ccOrganizationName}
                              variant="outlined"
                              size="small"
                              style={{ height: '20px', marginLeft: '5px' }}
                            />
                          )}
                        </TableCell>
                        {dmsTypeId != DmsTypeEnum.Infinity && (
                          <TableCell align="left">{row.isOrganization ? row.organizationName : ''}</TableCell>
                        )}
                        <TableCell align="left" style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {/* {row.cellPhoneVerificationStatus &&
                            row.cellPhoneVerificationStatus === PhoneVerificationStatusEnum.Verified ? (
                              <CheckCircleIcon className={classes.successIcon + ' successIcon'} />
                            ) : (
                              ''
                            )}
                            {row.cellPhoneVerificationStatus &&
                            row.cellPhoneVerificationStatus === PhoneVerificationStatusEnum.Failed ? (
                              <CancelIcon className={classes.errorIcon + ' errorIcon'} />
                            ) : (
                              ''
                            )} */}
                          {row.cellPhone ? formatPhoneNumber(row.cellPhone) : 'N/A'}
                        </TableCell>
                        <TableCell align="left">{row.emailAddress && <EmailLink email={row.emailAddress} />}</TableCell>
                        <TableCell align="left">{row.targetSource}</TableCell>
                        {dmsTypeId === DmsTypeEnum.Aspen && <TableCell align="left">{row.customerNumber}</TableCell>}
                        <TableCell align="left">
                          {row.createdAt ? format(new Date(row.createdAt), 'M/d/yyyy') : ''}{' '}
                        </TableCell>
                        {/* Hidding the status column for now as not required in the table */}
                        {/* <TableCell>{splitByCapitalLetters(row.customerStatus)}</TableCell> */}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      size="medium"
                      style={{ textAlign: 'center', fontSize: '15px', color: 'grey' }}>
                      No Customers Found
                    </TableCell>
                  </TableRow>
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell style={{ textAlign: 'center' }} colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            )}
            {apiLoader && (
              <TableBody>
                <TableRow style={{ height: 30 * 15, width: '100%' }}>
                  <TableCell style={{ textAlign: 'center' }} colSpan={12}>
                    <SmallCircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <Box textAlign="right" m={1}>
          <Pagination
            className={classes.pagination}
            count={Math.ceil(count / pageSize)}
            page={currentPage}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>
    </div>
  );
}
